// Recurring Invoice — the profile listing (spec §2) and the Recurring Invoice
// Profile Report (spec §4.1). Same columns; the listing has counters and live
// filters, the report has grouping, KPIs and a Run button.
import React, { useMemo, useState } from "react";
import {
  Btn,
  Counters,
  CustCell,
  DataGrid,
  DueCell,
  FDate,
  FSearch,
  FSelect,
  IC,
  PopMenu,
  RowBtn,
  Seg,
  StatusPill,
  TypeTag,
  opts,
} from "./RecurringParts";
import { INVOICE_TYPES, STATUSES, compact, counters, cycleWord, daysTo, fmtDLong, iso, monthKey, monthlyValue, money2, today } from "./recurringModel";
import { PROFILE_HEAD, exportTable, profileCells } from "./recurringExport";

export const BLANK_PF = { q: "", cust: "", sales: "", serv: "", type: "", from: "", to: "", status: "Active", counter: "" };

export const filterProfiles = (rows, f) => {
  const t = iso(today());
  const needle = (f.q || "").trim().toLowerCase();
  return rows.filter((p) => {
    if (f.status && p.status !== f.status) return false;
    if (f.cust && p.custGuid !== f.cust) return false;
    if (f.sales && p.salesGuid !== f.sales) return false;
    if (f.serv && p.servGuid !== f.serv) return false;
    if (f.type && p.type !== f.type) return false;
    if (f.from && (!p.next || p.next < f.from)) return false;
    if (f.to && (!p.next || p.next > f.to)) return false;
    if (f.counter === "dueToday" && p.next !== t) return false;
    if (f.counter === "dueWeek") {
      const n = daysTo(p.next);
      if (!(n !== null && n >= 0 && n <= 6)) return false;
    }
    if (f.counter === "failed" && !p.failed) return false;
    if (f.counter === "expiring" && !(p.end && monthKey(p.end) === monthKey(t))) return false;
    if (f.counter === "noemail" && !(p.autoEmail && !p.email)) return false;
    if (needle) {
      const hay = [p.no, p.custName, p.salesperson, p.service, p.type, p.warehouse].join(" ").toLowerCase();
      if (hay.indexOf(needle) < 0) return false;
    }
    return true;
  });
};

// distinct { v: guid, l: name } options from the loaded rows
export const distinct = (rows, gk, nk) => {
  const m = {};
  rows.forEach((r) => {
    if (r[gk]) m[r[gk]] = r[nk] || "—";
  });
  return Object.keys(m)
    .map((v) => ({ v, l: m[v] }))
    .sort((a, b) => a.l.localeCompare(b.l));
};

const groupOf = (p, g) => (g === "salesperson" ? p.salesperson : g === "service" ? p.service : p.type);
const amountOf = (p) => p.net;

const profileCols = (permit, act, withMore) => [
  { k: "custName", t: "Customer", render: (p) => <CustCell name={p.custName} sub={(p.no || "—") + " · " + cycleWord(p)} /> },
  { k: "salesperson", t: "Sales person", w: "130px" },
  { k: "service", t: "Main service", w: "190px", render: (p) => <span className="axrc-ell">{p.service || "—"}</span> },
  { k: "type", t: "Invoice type", w: "124px", render: (p) => <TypeTag t={p.type} /> },
  {
    k: "next",
    t: "Next invoice date",
    w: "190px",
    sortv: (p) => p.next || "9999",
    render: (p) => (
      <>
        <DueCell next={p.next} />
        {p.failed ? (
          <span className="axrc-pill failed" style={{ marginLeft: 5 }} title={p.lastRunMessage || "The last run failed"}>
            <span className="d" />
            Failed
          </span>
        ) : null}
      </>
    ),
  },
  { k: "net", t: "Amount", num: true, w: "112px", render: (p) => money2(p.net) },
  { k: "status", t: "Status", w: "110px", render: (p) => <StatusPill s={p.status} /> },
  {
    k: "acts",
    t: "",
    w: withMore ? "126px" : "98px",
    nosort: true,
    render: (p) => (
      <span className="axrc-rowacts">
        <RowBtn title="View" icon={IC.view} onClick={() => act.view(p)} />
        {permit.edit && <RowBtn title="Edit" icon={IC.edit} onClick={() => act.edit(p)} />}
        {permit.deletion && <RowBtn kind="del" title="Delete" icon={IC.del} onClick={() => act.del(p)} />}
        {withMore && (permit.edit || permit.add) && <RowBtn title="More" icon={IC.more} onClick={(e) => act.more(p, e.currentTarget)} />}
      </span>
    ),
  },
];

const ExportTools = ({ onExport, pdf }) => (
  <div className="axrc-btngrp">
    <Btn onClick={() => onExport("csv")}>CSV</Btn>
    <Btn onClick={() => onExport("xlsx")}>Excel</Btn>
    {pdf && <Btn onClick={() => onExport("pdf")}>PDF</Btn>}
    <Btn kind="icon" title="Print" aria-label="Print" icon={IC.print} onClick={() => onExport("print")} />
  </div>
);

const useMoreMenu = (permit, act) => {
  const [menu, setMenu] = useState(null);
  const open = (p, anchor) => {
    const items = [];
    if (permit.edit && (p.status === "Active" || p.status === "Paused"))
      items.push({ l: p.status === "Paused" ? "Resume profile" : "Pause profile", fn: () => act.pause(p) });
    if (permit.add && p.status === "Active") items.push({ l: "Create the invoice now", fn: () => act.makeNow(p) });
    if (permit.add) items.push({ l: "Clone this profile", fn: () => act.clone(p) });
    if (permit.edit && p.status !== "Cancelled") {
      items.push("-");
      items.push({ l: "Cancel profile", dgr: true, fn: () => act.cancel(p) });
    }
    if (items.length) setMenu({ anchor, items });
  };
  const node = menu ? <PopMenu anchor={menu.anchor} items={menu.items} onClose={() => setMenu(null)} /> : null;
  return [open, node];
};

/* ================================================================ listing */

export const ProfileList = ({ profiles, invoices, pf, setPf, permit, act, onReload, onGoInvoices }) => {
  const [openMore, menuNode] = useMoreMenu(permit, act);
  const rows = useMemo(() => filterProfiles(profiles, pf), [profiles, pf]);
  const c = useMemo(() => counters(profiles, invoices), [profiles, invoices]);
  const set = (k) => (v) => setPf((f) => ({ ...f, [k]: v, ...(k === "status" ? { counter: "" } : {}) }));
  const cols = useMemo(() => profileCols(permit, { ...act, more: openMore }, true), [permit, act, openMore]);

  const items = [
    { k: "", l: "Active", v: c.active, cls: "good", s: "being billed", on: pf.status === "Active" && !pf.counter },
    { k: "dueToday", l: "Due today", v: c.dueToday, cls: "acc", s: "raised by today's run", on: pf.counter === "dueToday" },
    { k: "dueWeek", l: "Due this week", v: c.dueWeek, s: "next seven days", on: pf.counter === "dueWeek" },
    { k: "genMonth", l: "Generated this month", v: c.genMonth, s: "invoices raised" },
    { k: "failed", l: "Failed", v: c.failed, cls: "bad", s: "needs a human", on: pf.counter === "failed" },
    { k: "expiring", l: "Expiring this month", v: c.expiring, cls: "warn", s: "contract ends", on: pf.counter === "expiring" },
    { k: "cancelled", l: "Cancelled", v: c.cancelled, s: "no longer billed", on: pf.status === "Cancelled" },
  ];
  const pick = (k) => {
    if (k === "genMonth") return onGoInvoices();
    if (k === "cancelled") return setPf((f) => ({ ...f, status: "Cancelled", counter: "" }));
    if (k === "expiring") return setPf((f) => ({ ...f, status: "", counter: "expiring" }));
    return setPf((f) => ({ ...f, status: "Active", counter: k }));
  };

  const doExport = (kind) => exportTable(kind, "recurring-profiles", "Recurring invoice profiles", PROFILE_HEAD, rows.map(profileCells));

  return (
    <section className="axrc-screen">
      <div className="axrc-phead">
        <div>
          <h1>Recurring invoice profiles</h1>
          <div className="sub">The standing instructions that generate invoices on their own</div>
        </div>
        <div className="sp" />
        <div className="axrc-btngrp">
          <Btn kind="icon" title="Refresh" aria-label="Refresh" icon={IC.refresh} onClick={onReload} />
        </div>
        <ExportTools onExport={doExport} />
        {permit.add && (
          <Btn kind="primary" icon={IC.plus} onClick={act.create}>
            New profile
          </Btn>
        )}
      </div>

      <Counters items={items} cols={7} onPick={pick} />

      <div className="axrc-filters">
        <FSearch id="rcPSearch" value={pf.q} onChange={set("q")} placeholder="Customer, profile no, service…" />
        <FSelect id="rcPCust" label="Customer" value={pf.cust} onChange={set("cust")} options={distinct(profiles, "custGuid", "custName")} all="All customers" />
        <FSelect id="rcPSales" label="Sales person" w="w1" value={pf.sales} onChange={set("sales")} options={distinct(profiles, "salesGuid", "salesperson")} all="All salespeople" />
        <FSelect id="rcPServ" label="Main service" value={pf.serv} onChange={set("serv")} options={distinct(profiles, "servGuid", "service")} all="All services" />
        <FSelect id="rcPType" label="Invoice type" w="w3" value={pf.type} onChange={set("type")} options={opts(INVOICE_TYPES)} all="All types" />
        <FDate id="rcPFrom" label="Next invoice from" value={pf.from} onChange={set("from")} />
        <FDate id="rcPTo" label="To" value={pf.to} onChange={set("to")} />
        <FSelect id="rcPStatus" label="Status" w="w3" value={pf.status} onChange={set("status")} options={opts(STATUSES)} all="All statuses" on />
        <div className="axrc-fl">
          <label>&nbsp;</label>
          <Btn onClick={() => setPf(BLANK_PF)}>Clear</Btn>
        </div>
      </div>

      <DataGrid
        cols={cols}
        rows={rows}
        rowKey={(p) => p.guid}
        initialSort={{ k: "next", dir: 1 }}
        onRowClick={act.view}
        emptyText={profiles.length ? "No profile matches these filters." : "No recurring profile yet."}
        foot={(r) => (
          <tr>
            <td colSpan={5}>Total · {r.length} profiles</td>
            <td className="num">{money2(r.reduce((a, p) => a + p.net, 0))}</td>
            <td colSpan={2} />
          </tr>
        )}
        footBar={
          <span>
            Recurring value <b>{money2(rows.reduce((a, p) => a + monthlyValue(p), 0))}</b> per month
          </span>
        }
      />
      {menuNode}
    </section>
  );
};

/* ================================================================ report */

export const BLANK_RPF = { cust: "", sales: "", serv: "", type: "", from: "", to: "", status: "Active" };

export const ProfileReport = ({ profiles, permit, act, cur }) => {
  // filters apply on "Run report", as on every other report in the app
  const [draft, setDraft] = useState(BLANK_RPF);
  const [applied, setApplied] = useState(BLANK_RPF);
  const [group, setGroup] = useState("");
  const [ranAt, setRanAt] = useState(new Date());
  const rows = useMemo(() => filterProfiles(profiles, applied), [profiles, applied]);
  const cols = useMemo(() => profileCols(permit, act, false), [permit, act]);
  const set = (k) => (v) => setDraft((f) => ({ ...f, [k]: v }));
  const mo = rows.reduce((a, p) => a + monthlyValue(p), 0);
  const due7 = rows.filter((p) => {
    const n = daysTo(p.next);
    return n !== null && n >= 0 && n <= 6;
  }).length;
  const doExport = (kind) =>
    exportTable(kind, "recurring-profile-report", "Recurring Invoice Profile Report", PROFILE_HEAD, rows.map(profileCells));

  return (
    <section className="axrc-screen">
      <div className="axrc-phead">
        <div>
          <h1>Recurring Invoice Profile Report</h1>
          <div className="sub">Standing instructions and what each one will bill next</div>
        </div>
        <div className="sp" />
        <Seg
          options={[
            { v: "", l: "No grouping" },
            { v: "salesperson", l: "By salesperson" },
            { v: "service", l: "By service" },
            { v: "type", l: "By type" },
          ]}
          value={group}
          onChange={setGroup}
        />
        <ExportTools onExport={doExport} pdf />
      </div>
      <div className="axrc-filters">
        <FSelect id="rcRpCust" label="Customer" value={draft.cust} onChange={set("cust")} options={distinct(profiles, "custGuid", "custName")} all="All customers" />
        <FSelect id="rcRpSales" label="Sales person" w="w1" value={draft.sales} onChange={set("sales")} options={distinct(profiles, "salesGuid", "salesperson")} all="All salespeople" />
        <FSelect id="rcRpServ" label="Main service" value={draft.serv} onChange={set("serv")} options={distinct(profiles, "servGuid", "service")} all="All services" />
        <FSelect id="rcRpType" label="Invoice type" w="w3" value={draft.type} onChange={set("type")} options={opts(INVOICE_TYPES)} all="All types" />
        <FDate id="rcRpFrom" label="Next invoice from" value={draft.from} onChange={set("from")} />
        <FDate id="rcRpTo" label="To" value={draft.to} onChange={set("to")} />
        <FSelect id="rcRpStatus" label="Status" w="w3" value={draft.status} onChange={set("status")} options={opts(STATUSES)} all="All statuses" on />
        <div className="sp" />
        <div className="axrc-fl">
          <label>&nbsp;</label>
          <Btn
            kind="primary"
            onClick={() => {
              setApplied(draft);
              setRanAt(new Date());
            }}
          >
            Run report
          </Btn>
        </div>
      </div>
      <Counters
        cols={4}
        items={[
          { k: "a", l: "Profiles", v: rows.length },
          { k: "b", l: "Value per cycle", v: cur + " " + compact(rows.reduce((a, p) => a + p.net, 0)) },
          { k: "c", l: "Normalised per month", v: cur + " " + compact(mo), cls: "good" },
          { k: "d", l: "Due in the next 7 days", v: due7, cls: "acc" },
        ]}
      />
      <DataGrid
        cols={cols}
        rows={rows}
        rowKey={(p) => p.guid}
        initialSort={{ k: "next", dir: 1 }}
        group={group}
        groupOf={groupOf}
        amountOf={amountOf}
        onRowClick={act.view}
        foot={(r) => (
          <tr>
            <td colSpan={5}>Report total · {r.length} profiles</td>
            <td className="num">{money2(r.reduce((a, p) => a + p.net, 0))}</td>
            <td colSpan={2} />
          </tr>
        )}
        footBar={
          <span>
            Run {fmtDLong(iso(ranAt))} · {applied.status || "all statuses"}
          </span>
        }
      />
    </section>
  );
};
